// @ts-nocheck
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Building2, Users, Pencil, Trash2, Loader2 } from 'lucide-react';
import { NewOrganizationDialog } from '@/components/admin/NewOrganizationDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

type OrgRow = {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  subdomain: string | null;
  is_active: boolean;
  member_count: number;
};

export default function AdminOrganizations() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [failedLogos, setFailedLogos] = useState<Record<string, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<OrgRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: organizations, isLoading, refetch } = useQuery({
    queryKey: ['platform-organizations'],
    queryFn: async () => {
      const { data: orgs, error } = await supabase
        .from('organizations')
        .select('id, name, slug, logo_url, subdomain, is_active')
        .order('name');
      if (error) throw error;

      const { data: members } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('status', 'active');

      const counts: Record<string, number> = {};
      (members || []).forEach((m: any) => {
        counts[m.organization_id] = (counts[m.organization_id] || 0) + 1;
      });

      return (orgs || []).map((o: any) => ({
        ...o,
        member_count: counts[o.id] || 0,
      })) as OrgRow[];
    },
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  const subLabel = (org: OrgRow) =>
    org.subdomain
      ? `${org.subdomain}.recruitlookevents.com`
      : org.slug
        ? `/${org.slug}`
        : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading text-foreground">Organizations</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage white-label client organizations.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" />
          New organization
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : organizations && organizations.length > 0 ? (
        <div className="flex flex-col gap-2">
          {organizations.map((org) => (
            <Card
              key={org.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/admin-dashboard/organizations/${org.id}`)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin-dashboard/organizations/${org.id}`); }}
              className="cursor-pointer rounded-xl border bg-card hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4 p-3">
                <div className="h-10 w-10 rounded-lg bg-muted border flex items-center justify-center overflow-hidden shrink-0">
                  {org.logo_url && !failedLogos[org.id] ? (
                    <img
                      src={org.logo_url}
                      alt={org.name}
                      className="h-full w-full object-cover"
                      onError={() => setFailedLogos((p) => ({ ...p, [org.id]: true }))}
                    />
                  ) : (
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{org.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {subLabel(org)}
                  </div>
                </div>

                <span className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
                  <Users className="h-3.5 w-3.5" />
                  {org.member_count} {org.member_count === 1 ? 'member' : 'members'}
                </span>

                {org.is_active ? (
                  <Badge className="rounded-full bg-green-100 text-green-700 hover:bg-green-100 border-0 shrink-0">
                    <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600 inline-block" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="rounded-full shrink-0">
                    <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground inline-block" />
                    Inactive
                  </Badge>
                )}

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Edit organization"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin-dashboard/organizations/${org.id}`);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label="Delete organization"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(org);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">No organizations yet</p>
          <Button onClick={() => setCreateOpen(true)} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Create your first organization
          </Button>
        </div>
      )}

      <NewOrganizationDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => refetch()} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-semibold">{deleteTarget?.name}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
